// src/components/PresensiExportSheets.js
import { apiFetch } from "../utils/wkCommon";

const HEADER = [
  "編號\nNomor",
  "姓名\nNama (Primary)",
  "印尼名/拼音\nNama (Secondary)",
  "性別\nGender",
  "年齡\nUsia",
  "助班費\nDonasi",
  "地區\nArea",
  "參與者類別\nKategori Partisipan",
  "電話\nTelepon",
  "住址\nAlamat",
  "天職\nJabatan Suci",
  "所屬佛堂\nFotang",
  "學歷\nPendidikan",
  "職業\nPekerjaan",
  "出生日期\nTanggal Lahir",
  "求道日期\nTanggal Qiudao",
  "引師\nYinshi",
  "保師\nBaoshi",
  "求道佛堂\nFotang Qiudao",
  "清口\nQingKou",
  "三天班\nSD 3 Hari",
  "明德\nMing De",
  "新民\nXin Min",
  "至善\nZhi Shan"
];

const PARTICIPANT_TYPE_MAP = {
  Peserta: { id: "Peserta", zh: "班員" },
  Petugas: { id: "Petugas", zh: "辦事" }
};

const GENDER_MAP = {
  Pria: { id: "Pria", zh: "乾道" },
  Wanita: { id: "Wanita", zh: "坤道" }
};

async function fetchAgendaAttributes(agendaId) {
  if (!agendaId) return {};

  const url = `/api/agendas/${encodeURIComponent(agendaId)}?populate%5B0%5D=agenda_book`;

  const resp = await apiFetch(url);
  const agenda = resp?.data || resp;

  const attrs = agenda?.agenda_book?.agendaAttributes || {};

  return attrs;
}

function parseSheetMeta(sheetName) {
  // contoh: "Peserta_Wanita"
  const [role, gender] = sheetName.split("_");

  return {
    role,
    gender: gender || null
  };
}

function computeAge(birthdate) {
  if (!birthdate) return "";
  const today = new Date();
  const dob = new Date(birthdate);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function normalizeGender(p) {
  const g =
    p.umat?.gender ||
    p.external_person?.gender ||
    p.nonUmatGender ||
    "";

  const lower = String(g).toLowerCase();

  if (lower.includes("pria") || lower.startsWith("m") || lower === "l")
    return "M";

  return "F";
}

function buildColumnsConfig(attrs) {
  const cols = [
    { key: "no", label: HEADER[0] },
    { key: "name", label: HEADER[1] },
    { key: "secondary", label: HEADER[2] },
    { key: "gender", label: HEADER[3] },
    { key: "age", label: HEADER[4] },
    { key: "donation", label: HEADER[5] },
    { key: "category", label: HEADER[7] },
  ];

  if (attrs.includeArea) cols.push({ key: "area", label: HEADER[6] });
  if (attrs.includePhone) cols.push({ key: "phone", label: HEADER[8] });
  if (attrs.includeAddress) cols.push({ key: "address", label: HEADER[9] });
  if (attrs.includeDutyLevel) cols.push({ key: "jabatan", label: HEADER[10] });
  if (attrs.includeFotang) cols.push({ key: "fotang", label: HEADER[11] });
  if (attrs.includeEducation) cols.push({ key: "education", label: HEADER[12] });
  if (attrs.includeOccupation) cols.push({ key: "occupation", label: HEADER[13] });

  if (attrs.includeBirthdate) cols.push({ key: "birthdate", label: HEADER[14] });
  if (attrs.includeQiudaoDate) cols.push({ key: "qiudaoDate", label: HEADER[15] });
  if (attrs.includeYinshi) cols.push({ key: "yinshi", label: HEADER[16] });
  if (attrs.includeBaoshi) cols.push({ key: "baoshi", label: HEADER[17] });
  if (attrs.includeFotangQiudao) cols.push({ key: "fotangQiudao", label: HEADER[18] });
  if (attrs.includeQingkou) cols.push({ key: "qingkou", label: HEADER[19] });
  if (attrs.includeSd3hari) cols.push({ key: "sd3hari", label: HEADER[20] });
  if (attrs.includeMingde) cols.push({ key: "mingde", label: HEADER[21] });
  if (attrs.includeXinmin) cols.push({ key: "xinmin", label: HEADER[22] });
  if (attrs.includeZhishan) cols.push({ key: "zhishan", label: HEADER[23] });

  return cols;
}

function resolveParticipantData(p) {
  const type = (p.personType || "").toLowerCase();

  const umat = p.umat || {};
  const ext = p.external_person || {};
  const non = {
    name: p.nonUmatName,
    gender: p.nonUmatGender,
    age: p.nonUmatAge,
    phone: p.nonUmatPhoneNo
  };

  const isInternal = type === "internal";
  const isExternal = type === "external";
  const isNonUmat = type === "nonumat";

  let primaryName = "", secondaryName = "";

  if (isInternal) {
    if (umat.chineseName) {
      primaryName = umat.chineseName;
      secondaryName = umat.pinyinName || umat.fullName || "";
    } else if (umat.pinyinName) {
      primaryName = umat.pinyinName;
      secondaryName = umat.fullName || umat.nickName || "";
    } else if (umat.fullName) {
      primaryName = umat.fullName;
      secondaryName = umat.nickName || "";
    }
  } else if (isExternal) {
    primaryName = ext.name || "";
  } else if (isNonUmat) {
    primaryName = non.name || "";
  }

  return {
    // ===== NAME =====
    primaryName,
    secondaryName,

    // ===== GENDER =====
    gender:
      (isInternal && umat.gender) ||
      (isExternal && ext.gender) ||
      (isNonUmat && non.gender) ||
      "",

    // ===== AGE =====
    age:
      (isInternal && computeAge(umat.birthdate)) ||
      (isExternal && ext.age) ||
      (isNonUmat && non.age) ||
      "",

    // ===== AREA =====
    area:
      umat.area?.shortname ||
      "",

    // ===== CATEGORY =====
    category:
      isInternal ? "Internal" :
      isExternal ? "External" :
      isNonUmat ? "Public" : "",

    // ===== PHONE =====
    phone:
      (isInternal && umat.mobilePhone) ||
      (isExternal && ext.phoneNumber) ||
      (isNonUmat && non.phone) ||
      "",

    // ===== ADDRESS =====
    address:
      (isInternal && umat.address) ||
      (isExternal && ext.address) ||
      "",

    // ===== JABATAN =====
    jabatan:
      (isInternal && umat.jabatanSuci) ||
      p.posName ||
      p.dutyLevel ||
      "",

    // ===== FOTANG =====
    fotang:
      (isInternal && "禮恩") ||
      (isExternal &&
        (ext.fotang_external?.fotangNameCH ||
         ext.fotangExternalOtherDesc)) ||
      "",

    // ===== EDUCATION =====
    education:
      (isInternal && umat.education) ||
      (isExternal && ext.education) ||
      "",

    // ===== OCCUPATION =====
    occupation:
      (isInternal && umat.occupation) ||
      (isExternal && ext.occupation) ||
      "",

    // ===== DATE =====
    birthdate: umat.birthdate || "",
    qiudaoDate: umat.tanggalMemohonTao || "",

    yinshi: umat.guruPengajakYinShi || "",
    baoshi: umat.guruPenjaminBaoShi || "",
    fotangQiudao: umat.fotangMemohonTao || "",

    qingkou: umat.qingKou || "",
    sd3hari: umat.sd3Hari || "",
    mingde: umat.mingde || "",
    xinmin: umat.xinmin || "",
    zhishan: umat.zhishan || ""
  };
}

function buildTitleRows(sheetName, agendaAttr) {
  const {
    agendaDateText,
    areaNameId,
    areaNameZh,
    agendaSubjectId,
    agendaSubjectZh,
    exportSplitOption
  } = agendaAttr || {};

  const { role, gender } = parseSheetMeta(sheetName);

  const roleMap = PARTICIPANT_TYPE_MAP[role] || {};
  const genderMap = GENDER_MAP[gender] || {};

  const showGender = exportSplitOption === "role_gender";

  // ================= LINE 1 =================
  const line1 = [agendaDateText || ""];

  // ================= LINE 2 =================
  const line2Parts = [
    areaNameZh,
    agendaSubjectZh,
    showGender ? genderMap.zh : null,
    roleMap.zh
  ].filter(Boolean);

  const line2 = [line2Parts.join(" ").trim()];

  // ================= LINE 3 =================
  const line3Parts = [
    "Daftar Hadir",
    agendaSubjectId,
    areaNameId,
    roleMap.id,
    showGender ? genderMap.id : null
  ].filter(Boolean);

  const line3 = [line3Parts.join(" ").trim()];

  return [line1, line2, line3];
}

function buildSheetsData(participants = [], agendaAttributes) {
  const sheets = {};

  function ensureSheet(name) {
    if (!sheets[name]) {
      sheets[name] = [];
    }
  }

  function getSheetName(p) {
    const role = p.peserta ? "Peserta" : "Petugas";
    const gender = normalizeGender(p) === "M" ? "Pria" : "Wanita";

    if (role === "Peserta") {
      return `${role}_${gender}`;
    }
    return "Petugas";
  }

  const clean = (participants || []).filter(p => !p.deleted);

  const cols = buildColumnsConfig(agendaAttributes);

  clean.forEach((p, idx) => {
    const sheetName = getSheetName(p);
    ensureSheet(sheetName);

    const d = resolveParticipantData(p);

    const row = cols.map(c => {
      switch (c.key) {
        case "no": return p.seqNo ?? idx + 1;
        case "name": return d.primaryName;
        case "secondary": return d.secondaryName;
        case "gender": return d.gender;
        case "age": return d.age;
        case "donation": return p.donationAmount || "";
        case "category": return d.category;
        default: return d[c.key] || "";
      }
    });

    sheets[sheetName].push(row);
  });

  // ================= FINAL STRUCTURE =================
  return Object.entries(sheets).map(([name, rows]) => {
    const titleRows = buildTitleRows(name, agendaAttributes);

    const headerRow = cols.map(c => c.label);

    return {
      name,
      values: [
        ...titleRows,
        [],
        headerRow,
        ...rows   // ✅ pakai langsung, jangan remap
      ]
    };
  });
}

export async function exportPresensiToGoogleSheet(
  participants,
  agenda
  ) {
  const agendaId = agenda?.documentId;

  const agendaAttributes = await fetchAgendaAttributes(agendaId);

  const sheets = buildSheetsData(participants, agendaAttributes);

  // ===== FILE NAME (tetap pakai logic kamu, nanti bisa disesuaikan lagi) =====
  const date = agenda?.agendaDate || "";
  const d = date ? new Date(date) : null;

  const ymd = d
    ? `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`
    : Date.now();

  const subject = agenda?.subject || "Presensi";
  const area = agenda?.area?.name || "NA";
  const uid = agenda?.documentId?.slice(-6) || Date.now();

  //const fileName = `${ymd} ${area} ${subject} ${uid}`;
  const fileName = `PRESENSI_${ymd} ${area} ${subject} ${agendaId}`;

  // ================= STYLE =================
  const colCount = sheets[0]?.values?.[3]?.length || HEADER.length;

  const style = {
    default: {
      titleRows: [0,1,2],
      separatorRows: [3],
      headerRows: [4],
      contentStartRow: 5,

      titleStyle: {
        bold: true,
        fontSize: 14,
        align: "CENTER"
      },

      headerStyle: {
        bold: true,
        fontSize: 12,
        bgColor: "#f3f4f6",
        textColor: "#111827",
        align: "CENTER"
      },

      contentStyle: {
        fontSize: 11,
        zebra: true,
        zebraColor: "#fafafa"
      },

      columns: Array.from({ length: colCount }).map((_, i) => ({
        index: i,
        align: i === 1 ? "LEFT" : "CENTER",
        type: i === 5 ? "CURRENCY" : "TEXT"
      }))
    }
  };

  /*
    overrides: {
      Petugas: {
        columns: HEADER.map((_, i) => ({
          index: i,
          align: i === 1 ? "LEFT" : "CENTER",
          type: "TEXT"
        }))
      }
    }
  */

  const payload = {
    contentType: "presensi",
    documentId: fileName,
    formatName: "Daftar-Hadir",
    file: { type: "spreadsheet" },
    sheets,
    style
    //, clearBeforeWrite: true
  };

  const resp = await apiFetch("/api/google/execute", {
    method: "POST",
    body: payload
  });

  return resp;
}
